import React, { useEffect, useState } from 'react';
import {
  Page,
  Card,
  Layout,
  Text,
  BlockStack,
  Button,
  Banner,
  Spinner,
  InlineStack,
  Badge,
  Divider,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isLocalDevelopment } from '../utils/env';
import CrownIcon from '../components/CrownIcon';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';

// Hook seguro para usar App Bridge
function useAppBridgeSafe() {
  try {
    return useAppBridge();
  } catch {
    return null;
  }
}

interface SubscriptionStatus {
  plan: 'free' | 'paid';
  status: 'free' | 'active' | 'cancelled' | 'pending';
  charge?: any;
  source?: 'shopify' | 'manual' | 'default';
}

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const { shopId } = useAuth();
  const navigate = useNavigate();
  const app = useAppBridgeSafe();
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    fetchSubscriptionStatus();
  }, [shopId]);

  const fetchSubscriptionStatus = async () => {
    if (!shopId) return;

    setLoading(true);
    setError(null);

    // Em desenvolvimento local, simular plano FREE
    if (isLocalDevelopment()) {
      setSubscription({
        plan: 'free',
        status: 'free',
      });
      setLoading(false);
      return;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
        {
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Se der erro, assumir plano FREE
        setSubscription({
          plan: 'free',
          status: 'free',
        });
        setLoading(false);
        return;
      }

      const data = await response.json();
      setSubscription(data);
    } catch (err) {
      // Em caso de erro, assumir plano FREE
      setSubscription({
        plan: 'free',
        status: 'free',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!shopId) return;

    // Em desenvolvimento local, mostrar mensagem
    if (isLocalDevelopment()) {
      setError('Em desenvolvimento local, a assinatura só funciona em produção com uma loja Shopify real.');
      return;
    }

    setSubscribing(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
        {
          method: 'POST',
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plan: 'paid' }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Se já tem assinatura ativa, mostrar mensagem específica
        if (errorData.hasActiveSubscription) {
          throw new Error('You already have an active subscription. Your subscription is currently active and renewing automatically.');
        }
        
        const errorMessage = errorData.error || 
                            errorData.details || 
                            `Failed to create subscription (Status: ${response.status})`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Validar que temos confirmationUrl
      if (!data.confirmationUrl) {
        throw new Error(data.error || 'No confirmation URL received from server. Please try again.');
      }
      
      // Redirecionar para página de pagamento usando App Bridge
      setSubscribing(false);
      
      // Usar App Bridge Redirect se disponível, senão usar window.location
      if (app) {
        try {
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, data.confirmationUrl);
        } catch (redirectError) {
          console.error('App Bridge redirect error:', redirectError);
          // Fallback: redirecionar a janela principal se não estiver embedado
          if (window.self !== window.top) {
            window.top!.location.href = data.confirmationUrl;
          } else {
            window.location.href = data.confirmationUrl;
          }
        }
      } else {
        // Fallback: redirecionar a janela principal se não estiver embedado
        if (window.self !== window.top) {
          window.top!.location.href = data.confirmationUrl;
        } else {
          window.location.href = data.confirmationUrl;
        }
      }
    } catch (err) {
      console.error('Subscription error:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'An unexpected error occurred. Please try again or contact support if the problem persists.';
      
      // Se o erro indica que já tem assinatura ativa, recarregar status
      if (errorMessage.includes('already have an active subscription')) {
        await fetchSubscriptionStatus();
      }
      
      setError(errorMessage);
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!shopId) return;
    
    // Confirmar cancelamento
    if (!confirm(t('billing.cancelConfirm', { 
      defaultValue: 'Tem certeza que deseja cancelar sua assinatura? Você perderá acesso aos recursos PRO.' 
    }))) {
      return;
    }

    setCancelling(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
        {
          method: 'POST',
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'cancel' }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      // Recarregar status da assinatura
      await fetchSubscriptionStatus();
    } catch (err) {
      console.error('Cancel error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <BlockStack gap="300" align="center">
                  <Spinner size="large" />
                  <Text as="p" tone="subdued" alignment="center">
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </Text>
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const isPaid = subscription?.plan === 'paid' && subscription?.status === 'active';

  return (
    <Page
      title={t('billing.title', { defaultValue: 'Assinatura' })}
      backAction={{ onAction: () => navigate('/') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Conteúdo normal */}
        <>

        {/* Banner de Upgrade quando está no plano FREE */}
        {!isPaid && (
          <Layout.Section>
            <Banner
              tone="info"
              title={t('billing.upgradeBanner.title', { defaultValue: 'Upgrade para Plano Pro' })}
              action={{
                content: t('billing.upgradeBanner.action', { defaultValue: 'Ver Planos' }),
                onAction: () => {
                  // Scroll para seção de planos
                  setTimeout(() => {
                    const plansSection = document.getElementById('plans-section');
                    if (plansSection) {
                      plansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                },
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <CrownIcon size={20} />
                <p style={{ margin: 0 }}>
                  {t('billing.upgradeBanner.message', { 
                    defaultValue: 'Você está no plano gratuito. Faça upgrade para desbloquear produtos ilimitados e recursos avançados!' 
                  })}
                </p>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {/* Banner de Agradecimento quando está no plano PRO */}
        {isPaid && (
          <Layout.Section>
            <Banner
              tone="success"
              title={t('billing.proBanner.title', { defaultValue: 'Plano Pro Ativo' })}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <CrownIcon size={24} />
                <p style={{ margin: 0 }}>
                  {t('billing.proBanner.message', { 
                    defaultValue: 'Obrigado por ser um cliente Pro! Você tem acesso a todos os recursos avançados e recebe novas funcionalidades primeiro.' 
                  })}
                </p>
              </div>
              <p>
                {t('billing.proBanner.message', { 
                  defaultValue: 'Obrigado por ser um cliente Pro! Você tem acesso a todos os recursos avançados e recebe novas funcionalidades primeiro.' 
                })}
              </p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text variant="headingLg" as="h2">
                      {t('billing.currentPlan', { defaultValue: 'Plano Atual' })}
                    </Text>
                    {!isPaid && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t('billing.freePlanNote', { 
                          defaultValue: 'Você está no plano gratuito. Pode fazer upgrade a qualquer momento!' 
                        })}
                      </Text>
                    )}
                    {isPaid && (
                      <Text as="p" variant="bodySm" tone="success" fontWeight="medium">
                        {t('billing.paidPlanNote', { 
                          defaultValue: 'Sua assinatura está ativa e renovando automaticamente. Você tem acesso a todos os recursos PRO.' 
                        })}
                      </Text>
                    )}
                  </BlockStack>
                  {isPaid ? (
                    <InlineStack gap="200" blockAlign="center">
                      <CrownIcon size={20} />
                      <Badge 
                        tone="success" 
                        size="large"
                        progress="complete"
                      >
                        {t('billing.paid', { defaultValue: 'PRO' })}
                      </Badge>
                    </InlineStack>
                  ) : (
                    <Badge 
                      tone="info" 
                      size="large"
                    >
                      {t('billing.free', { defaultValue: 'GRÁTIS' })}
                    </Badge>
                  )}
                </InlineStack>
              </BlockStack>

              <Divider />

              {/* Plano FREE */}
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                      <InlineStack gap="200" blockAlign="center">
                        <Text variant="headingMd" as="h3">
                          {t('billing.freePlan.title', { defaultValue: 'Plano Grátis' })}
                        </Text>
                        {!isPaid && (
                          <Badge tone="info" size="small">
                            {t('billing.current', { defaultValue: 'Atual' })}
                          </Badge>
                        )}
                      </InlineStack>
                      <Text variant="heading2xl" as="p" tone="subdued">
                        {t('billing.free', { defaultValue: 'Grátis' })}
                        <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>
                          {' '}{t('billing.forever', { defaultValue: 'para sempre' })}
                        </span>
                      </Text>
                    </BlockStack>
                  </InlineStack>
                  <Text as="p" tone="subdued">
                    {t('billing.freePlan.description', { 
                      defaultValue: 'Tudo que você precisa para começar, sem custos' 
                    })}
                  </Text>
                </BlockStack>

                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature1', { defaultValue: 'Avaliações ilimitadas' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature2', { defaultValue: 'Widget de avaliações completo' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature3', { defaultValue: 'Dashboard com métricas em tempo real' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature4', { defaultValue: 'Moderação manual de avaliações' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature5', { defaultValue: 'Badge "Compra Verificada"' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature6', { defaultValue: 'Verificação automática de compra' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature7', { defaultValue: 'Widget responsivo' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.freePlan.feature8', { defaultValue: 'Suporte por email' })}
                  </Text>
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    ⚠️ {t('billing.freePlan.limit', { defaultValue: 'Até 30 produtos' })}
                  </Text>
                </BlockStack>
              </BlockStack>

              <Divider />

              {/* Plano PAID */}
              <div id="plans-section">
                <BlockStack gap="400">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="start">
                      <BlockStack gap="050">
                        <InlineStack gap="200" blockAlign="center">
                          <CrownIcon size={24} />
                          <Text variant="headingMd" as="h3">
                            {t('billing.paidPlan.title', { defaultValue: 'Plano Pro' })}
                          </Text>
                          {isPaid && (
                            <Badge tone="success" size="small">
                              {t('billing.active', { defaultValue: 'Ativo' })}
                            </Badge>
                          )}
                        </InlineStack>
                        <Text variant="heading2xl" as="p">
                          $9.99<span style={{ fontSize: '1rem', fontWeight: 'normal' }}>/mês</span>
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t('billing.cancelAnytime', { defaultValue: 'Cancele a qualquer momento' })}
                        </Text>
                      </BlockStack>
                    {!isPaid && (
                      <Button
                        variant="primary"
                        onClick={handleSubscribe}
                        loading={subscribing}
                        size="large"
                      >
                        {t('billing.subscribe', { defaultValue: 'Assinar Agora' })}
                      </Button>
                    )}
                    {isPaid && (
                      <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center">
                          <Badge tone="success" size="large">
                            {t('billing.active', { defaultValue: 'Ativo' })}
                          </Badge>
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="success" fontWeight="medium">
                          {t('billing.subscriptionActive', { 
                            defaultValue: 'Sua assinatura está ativa e renovando automaticamente.' 
                          })}
                        </Text>
                        <Button
                          variant="secondary"
                          tone="critical"
                          onClick={handleCancel}
                          loading={cancelling}
                          size="medium"
                        >
                          {t('billing.cancel', { defaultValue: 'Cancelar Assinatura' })}
                        </Button>
                      </BlockStack>
                    )}
                  </InlineStack>
                  <Text as="p" tone="subdued">
                    {t('billing.paidPlan.description', { 
                      defaultValue: 'Recursos avançados para maximizar suas vendas e SEO' 
                    })}
                  </Text>
                </BlockStack>

                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {t('billing.paidPlan.includesFree', { defaultValue: 'Tudo do Plano Grátis, mais:' })}
                  </Text>
                  <Text as="p" variant="bodyMd" fontWeight="semibold" tone="success">
                    ⭐ {t('billing.paidPlan.unlimitedProducts', { defaultValue: 'Produtos ILIMITADOS' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature1', { defaultValue: 'Rich Snippets para SEO (estrelas no Google)' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature2', { defaultValue: 'Widget personalizado e customizável' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature3', { defaultValue: 'Relatórios avançados e analytics' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature4', { defaultValue: 'Importação avançada de avaliações' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature5', { defaultValue: 'Suporte prioritário' })}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ✅ {t('billing.paidPlan.feature6', { defaultValue: 'Novas funcionalidades primeiro' })}
                  </Text>
                </BlockStack>

                </BlockStack>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>
        </>
      </Layout>
    </Page>
  );
};

export default Billing;

